/**
 * Which build of the console and of the two backends is running, so a deployment can be checked
 * at a glance. The console's own build is baked into the bundle; the backends answer `GET /version`.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { translate } from 'react-jhipster';

interface BuildInfo {
  version: string;
  commit?: string | null;
  builtAt?: string | null;
}

type Status = BuildInfo | 'loading' | 'unavailable';

const baseUrl = (url?: string, port?: string): string => `${url || 'http://localhost'}${port ? `:${port}` : ''}`;

const BACKENDS = [
  { name: 'Data Manager', url: `${baseUrl(process.env.DATA_MANAGER_URL, process.env.DATA_MANAGER_PORT)}/version` },
  {
    name: 'Config Manager',
    url: `${baseUrl(process.env.CONFIG_MANAGER_URL, process.env.CONFIG_MANAGER_PORT)}/version`,
  },
];

const CONSOLE: BuildInfo = { version: VERSION, commit: BUILD_COMMIT || null, builtAt: BUILD_TIME || null };

const Line = ({ name, status }: { name: string; status: Status }) => {
  let text: string;
  let builtAt: string | null = null;
  if (status === 'loading') text = '…';
  else if (status === 'unavailable') text = translate('profile-section.version-unavailable');
  else {
    text = status.commit ? `${status.version} · ${status.commit}` : status.version;
    builtAt = status.builtAt ?? null;
  }
  const line = (
    <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.6 }}>
      {name}{' '}
      <Box component="span" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
        {text}
      </Box>
    </Typography>
  );
  return builtAt ? (
    <Tooltip title={translate('profile-section.version-built-at', { date: builtAt })} placement="left">
      {line}
    </Tooltip>
  ) : (
    line
  );
};

export const AppVersions = ({ open, width }: { open: boolean; width?: number }) => {
  const [backends, setBackends] = useState<Status[]>(BACKENDS.map(() => 'loading'));

  // Asked again each time the menu opens, so a redeploy shows without reloading the page.
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    BACKENDS.forEach(({ url }, index) => {
      axios
        .get<BuildInfo>(url, { timeout: 5000 })
        .then(({ data }) => data)
        .catch((): Status => 'unavailable')
        .then(status => {
          if (!alive) return;
          setBackends(previous => previous.map((current, i) => (i === index ? status : current)));
        });
    });
    return () => {
      alive = false;
    };
  }, [open]);

  return (
    <Stack sx={{ width, px: 2, pt: 1 }} spacing={0}>
      <Typography variant="caption" sx={{ fontWeight: 600 }} color="text.secondary">
        {translate('profile-section.versions')}
      </Typography>
      <Line name="ScoringJoe" status={CONSOLE} />
      {BACKENDS.map(({ name }, index) => (
        <Line key={name} name={name} status={backends[index]} />
      ))}
    </Stack>
  );
};
